import { capitalizeFirstLetter, normalizeSpaces, sentanceCase } from './friendly';

/**
 * Returns a user-friendly drug-class string.
 * Currently this simply converts the raw value to sentence-case and fixes
 * a common miss-spelling/variant ("beta-lactamese" → "Beta-lactamese").
 * Extend this function if more special-cases arise in the future.
 */
export function friendlyDrugClass(raw: string | null | undefined): string {
  if (!raw) return '';
  const base = sentanceCase(raw);
  return base.replace(/beta-lactam/gi, 'Beta-lactam');
}

/**
 * Returns a user-friendly resistance-mechanism string.
 * Performs the same normalisation rules as {@link friendlyDrugClass}.
 */
export function friendlyResistanceMechanism(raw: string | null | undefined): string {
  if (!raw) return '';
  const base = sentanceCase(raw);
  return base.replace(/beta-lactam/gi, 'Beta-lactam');
}

/**
 * Returns a user-friendly AMR family string.
 * Developed against o3 cleaned AMR family names.
 */
export function friendlyAMRFamily(rawName: string): string {
  const trimmed = rawName.trim();

  let mutatingName = normalizeSpaces(trimmed);

  mutatingName = mutatingName
    .replace(/\b16s\b/gi, '16S')
    .replace(/\b23s\b/gi, '23S')
    .replace(/\b50s\b/gi, '50S');

  if (/^antibiotic resistant ndh$/i.test(mutatingName)) {
    return 'Antibiotic resistant ndh';
  }
  if (/^antibiotic resistant nfsA$/i.test(mutatingName)) {
    return 'Antibiotic resistant nfsA';
  }
  if (/^antibiotic resistant ergosterol biosynthesis pathway transcription factor$/i.test(mutatingName)) {
    return 'Antibiotic resistant ergosterol biosynthesis transcription factor';
  }
  if (/^antibiotic-resistant UhpT$/i.test(mutatingName)) {
    return 'Antibiotic-resistant UhpT';
  }
  if (/^daptomycin resistant cls$/i.test(mutatingName)) {
    return 'Daptomycin resistance';
  }
  mutatingName = mutatingName.replace(/^echinocandin antibiotic-resistant/i, 'Echinocandin-resistant');
  mutatingName = mutatingName.replace(/-encoding /i, ' ');
  if (/^Target protecting FusB-type protein conferring resistance to Fusidic acid$/i.test(mutatingName)) {
    return 'FusB-type fusidic acid resistance protein';
  }
  if (/^Outer Membrane Porin( \(Opr\))?$/i.test(mutatingName)) {
    return 'Outer membrane porin';
  }
  if (/^Sugar Porin( \(SP\))?$/i.test(mutatingName)) {
    return 'Sugar porin';
  }
  mutatingName = mutatingName.replace(/^RbpA bacterial /i, 'RbpA ');
  if (/^small multidrug resistance (?:\(SMR\) )?antibiotic efflux pump$/i.test(mutatingName)) {
    return 'Small multidrug resistance efflux pump';
  }
  mutatingName = mutatingName.replace(/ dfr$/i, '');
  mutatingName = mutatingName.replace(' related proteins', ' proteins');

  mutatingName = mutatingName.replace(
    /^(\d+S rRNA|ampR transcriptional regulator) with mutation conferring resistance to /i,
    (_m, prefix) => `${prefix} with resistance to `,
  );

  mutatingName = mutatingName.replace(/\s+\(([^)]*)\)/g, (_m, content) =>
    /\d/.test(String(content)) ? ` ${String(content)}` : '',
  );

  mutatingName = mutatingName.replace(/with mutation conferring resistance to/gi, 'with resistance to');
  mutatingName = mutatingName.replace(
    /mutations conferring resistance to ([^ ]+)(?: antibiotics)?/i,
    'mutations with $1 resistance',
  );
  mutatingName = mutatingName.replace(
    /mutation conferring resistance to ([^ ]+)(?: antibiotics)?/i,
    'mutation with $1 resistance',
  );
  mutatingName = mutatingName.replace(/mutation conferring resistance to/gi, 'resistant to');
  mutatingName = mutatingName.replace(
    /pending classification for carbapenemase activity/i,
    'pending carbapenemase classification',
  );
  mutatingName = mutatingName.replace(/Lpx gene conferring resistance/i, 'Lpx conferring resistance');
  mutatingName = mutatingName.replace(/Resistance Protein$/, 'resistance protein');
  mutatingName = mutatingName.replace(/ Protein$/, ' protein');
  mutatingName = mutatingName.replace(/methyltrasnferase/i, 'methyltransferase');
  mutatingName = mutatingName.replace(/Helicobater/i, 'Helicobacter');
  mutatingName = mutatingName.replace(/16S RNA mutants/i, '16S rRNA mutants');
  mutatingName = mutatingName.replace(/mutants conferring resistance to ([^ ]+)/i, 'with $1 resistance');
  mutatingName = mutatingName.replace(/mutations with conferring resistance to/i, 'with resistance to');
  mutatingName = mutatingName.replace(/ subfamily ATP-binding cassette /i, ' ');
  mutatingName = mutatingName.replace(/with with /i, 'with ');

  mutatingName = mutatingName.replace(
    /^16S rRNA with resistance to (pactamycin|tetracycline derivatives)/i,
    (_m, antibiotic) => `16S rRNA resistant to ${antibiotic}`,
  );
  mutatingName = mutatingName.replace(
    /^23S rRNA with resistance to (lincosamide|linezolid|pleuromutilin)/i,
    (_m, antibiotic) => `23S rRNA resistant to ${antibiotic}`,
  );
  mutatingName = mutatingName.replace(/^50S rRNA with resistance to macrolide/i, '50S rRNA resistant to macrolide');
  mutatingName = mutatingName.replace(
    /^ampR transcriptional regulator with resistance to /i,
    'AmpR transcriptional regulator resistant to ',
  );

  mutatingName = mutatingName.replace(/^mutant\s+/i, '');
  if (/^General Bacterial Porin with reduced permeability to peptide antibiotics/i.test(mutatingName)) {
    mutatingName = mutatingName.replace(/^General Bacterial Porin/i, 'General bacterial porin');
  } else {
    mutatingName = mutatingName.replace(/^General Bacterial Porin/i, 'Bacterial porin');
  }

  mutatingName = mutatingName.replace(/peptide antibiotics/gi, 'peptide antibs');
  mutatingName = mutatingName.replace(/ to antibiotics/gi, ' to antibs');
  mutatingName = mutatingName.replace(/ antibiotics/gi, '');

  mutatingName = mutatingName.replace(/^antibiotic[- ]resistant\s+/i, 'Resistant ');

  mutatingName = mutatingName.replace(/^subclass\s+/i, '');

  mutatingName = mutatingName.replace(/beta[ -]lactamase(s?)/gi, (_, plural) => `Beta-lactamase${plural}`);

  const lowerCaseAllowed = ['blaF family Beta-lactamase', 'blaS', 'pH-responsive transcription factors', 'vanS'];
  if (!lowerCaseAllowed.includes(mutatingName)) {
    mutatingName = capitalizeFirstLetter(mutatingName);
  }

  return mutatingName;
}
